/**
 * Client Billing API Service
 *
 * API service for client organization billing operations:
 * - Billing overview, invoices, payment history
 * - Payment method management (Stripe Elements integration)
 * - Usage consumption tracking
 * - Auto-recharge threshold configuration
 */

import { config as productConfig } from '@/src/config';

// Product-aware base URL — Triveny → ms-api-hots, WeaveIn → weavein-api.
// Direct VITE_WEAVEIN_API_URL reads hit the wrong backend on Triveny.
const API_BASE = (productConfig.weaveinApi?.baseUrl ?? 'https://weavein-api.onrender.com').trim();

const getAuthHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;
  return headers;
};
const BILLING_PREFIX = `${API_BASE}/api/client-billing`;

// ============================================================================
// TYPES
// ============================================================================

// Payment Methods
export interface PaymentMethod {
  id: string;
  method_type: 'card' | 'bank_account' | 'ach' | 'wire' | 'check' | 'other';
  card_brand?: string;
  card_last_four?: string;
  card_exp_month?: number;
  card_exp_year?: number;
  bank_name?: string;
  account_last_four?: string;
  account_type?: string;
  is_default: boolean;
  stripe_payment_method_id?: string;
  created_at?: string;
}

// Billing Customer
export interface BillingCustomer {
  id: string;
  company_name: string;
  billing_email: string;
  stripe_customer_id?: string;
  default_payment_method_id?: string;
  credit_balance: number;
  status: string;
  currency: string;
}

// Subscription
export interface BillingSubscription {
  id: string;
  subscription_number: string;
  plan_name: string;
  plan_code?: string;
  pricing_model?: string;
  status: 'trialing' | 'active' | 'past_due' | 'paused' | 'canceled' | 'expired';
  quantity: number;
  unit_price?: number;
  plan_unit_name?: string;
  mrr?: number;
  arr?: number;
  currency: string;
  current_period_start?: string;
  current_period_end?: string;
  billing_interval: string;
  start_date?: string;
  trial_start?: string;
  trial_end?: string;
  auto_renew: boolean;
  contract_start?: string;
  contract_end?: string;
  plan_features?: any[];
  base_price?: number;
  included_units?: number;
  setup_fee?: number;
}

// Invoice
export interface BillingInvoice {
  id: string;
  invoice_number: string;
  status: 'draft' | 'open' | 'sent' | 'viewed' | 'overdue' | 'paid' | 'void' | 'uncollectible';
  invoice_date?: string;
  due_date?: string;
  subtotal: number;
  discount_total: number;
  tax_total: number;
  total: number;
  amount_due: number;
  amount_paid: number;
  amount_remaining?: number;
  currency: string;
  period_start?: string;
  period_end?: string;
  has_pdf: boolean;
  /** Hosted payment portal URL (unified org_invoices). Present once the invoice is sent. */
  portal_url?: string;
  paid_at?: string;
  created_at?: string;
}

export interface InvoiceLineItem {
  id: string;
  line_type: string;
  description: string;
  quantity: number;
  unit_price?: number;
  amount: number;
  discount_amount: number;
  tax_amount: number;
  net_amount: number;
  period_start?: string;
  period_end?: string;
}

export interface InvoiceDetail {
  invoice: BillingInvoice & {
    subscription_plan_name?: string;
    memo?: string;
    payment_terms?: string;
    stripe_invoice_id?: string;
    invoice_pdf_url?: string;
  };
  line_items: InvoiceLineItem[];
  payments: PaymentRecord[];
}

// Payments
export interface PaymentRecord {
  id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method_type?: string;
  card_brand?: string;
  card_last_four?: string;
  invoice_number?: string;
  processed_at?: string;
  failure_message?: string;
  refunded_amount: number;
  receipt_url?: string;
  created_at?: string;
}

// Usage
export interface UsageMeter {
  meter_code: string;
  meter_name: string;
  aggregation_type: string;
  total_usage: number;
  event_count: number;
}

export interface CommunicationPricing {
  channel: string;
  display_name: string;
  unit_name: string;
  cost_per_unit: number;
  included_units: number;
  customer_threshold: number;
  overage_rate?: number;
}

export interface AiTokenPricing {
  service_name: string;
  service_code: string;
  model_name?: string;
  input_rate_per_million: number;
  output_rate_per_million: number;
  included_credits_per_month: number;
}

export interface UsageSummary {
  period: string;
  usage: UsageMeter[];
  communication_pricing: CommunicationPricing[];
  ai_pricing: AiTokenPricing[];
  credit_balances: Record<string, number>;
}

// Billing Overview
export interface BillingOverview {
  customer: BillingCustomer;
  subscription: BillingSubscription | null;
  upcoming_invoice: {
    id: string;
    invoice_number: string;
    status: string;
    total: number;
    amount_due: number;
    amount_remaining?: number;
    due_date?: string;
    currency: string;
  } | null;
  credit_balances: Record<string, number>;
  recent_payments_30d: {
    count: number;
    total_paid: number;
  };
}

// Plan Tiers
export interface PlanTier {
  id: string;
  tier_code: string;
  tier_name: string;
  description?: string;
  base_monthly_price: number;
  per_user_price: number;
  annual_discount_percent: number;
  min_users: number;
  max_users?: number;
  capabilities: any[];
  features: any[];
  included_communication_credits: number;
  included_ai_credits: number;
  volume_discounts: any[];
  is_popular: boolean;
  badge_text?: string;
}

// Consumption Thresholds
export interface ConsumptionThreshold {
  id: string;
  service_category: 'communication' | 'ai_token';
  threshold_type: string;
  low_balance_threshold?: number;
  auto_recharge_threshold?: number;
  recharge_amount?: number;
  max_monthly_cap?: number;
  current_month_recharged: number;
  alert_enabled: boolean;
  alert_channels: { email: boolean; sms: boolean };
  alert_email?: string;
  alert_phone?: string;
  alert_at_percentage: number;
  is_enabled: boolean;
  last_alert_sent_at?: string;
  last_recharge_at?: string;
  last_recharge_amount?: number;
  payment_method_id?: string;
}

// Setup Intent Response
export interface SetupIntentResponse {
  client_secret: string;
  setup_intent_id: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function fetchBilling<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...options.headers,
  };

  const response = await fetch(`${BILLING_PREFIX}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

// --- Billing Overview ---

/** Optional cboId scopes the view to a specific org (admins/operators viewing another org). */
export async function getBillingOverview(cboId?: number): Promise<BillingOverview> {
  return fetchBilling(`/my-billing/overview${cboId ? `?cbo_id=${cboId}` : ''}`);
}

// --- Transaction fees (effective per-org fees: override or platform default) ---

export interface TransactionFee {
  service_type: string;
  label: string;
  fee_type: string; // percent_plus_fixed | percent | flat
  fee_percent: number; // fraction, e.g. 0.029 = 2.9%
  fee_fixed_cents: number;
  billing_model: string; // at_source | invoiced
  source: string; // org_override | env_default
}

export async function getMyTransactionFees(cboId?: number): Promise<{ cbo_id: number; fees: TransactionFee[] }> {
  return fetchBilling(`/my-billing/fees${cboId ? `?cbo_id=${cboId}` : ''}`);
}

/** Human label for an effective fee, e.g. "2.90% + $0.30" or "Billed by invoice". */
export function formatTransactionFee(f: TransactionFee): string {
  if (f.billing_model === 'invoiced') return 'Billed by invoice';
  const parts: string[] = [];
  if (f.fee_type !== 'flat') parts.push(`${(f.fee_percent * 100).toFixed(2)}%`);
  if (f.fee_type !== 'percent' && f.fee_fixed_cents > 0) parts.push(`$${(f.fee_fixed_cents / 100).toFixed(2)}`);
  return parts.length ? parts.join(' + ') : 'No fee';
}

// --- Invoices ---

export async function getInvoices(params?: {
  status?: string;
  page?: number;
  page_size?: number;
  cboId?: number;
}): Promise<{ invoices: BillingInvoice[]; total: number; page: number; page_size: number }> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.page_size) searchParams.set('page_size', String(params.page_size));
  if (params?.cboId) searchParams.set('cbo_id', String(params.cboId));
  const qs = searchParams.toString();
  return fetchBilling(`/my-billing/invoices${qs ? `?${qs}` : ''}`);
}

export async function getInvoiceDetail(invoiceId: string): Promise<InvoiceDetail> {
  return fetchBilling(`/my-billing/invoices/${invoiceId}`);
}

/**
 * Render-ready payload for the formatted invoice — the same shape the operator
 * invoice page's InvoicePreview consumes (merges the issuer's template branding /
 * bank / payment details). `cboId` scopes to a client org for admin viewers.
 */
export async function getInvoicePreview(invoiceId: string | number, cboId?: number): Promise<any> {
  return fetchBilling(`/my-billing/invoices/${invoiceId}/preview${cboId ? `?cbo_id=${cboId}` : ''}`);
}

/**
 * Render an invoice PDF on demand and open it in a new tab.
 * The endpoint streams application/pdf (finalized invoices aren't pre-rendered to
 * a stored URL), so we fetch it as a blob with auth and open an object URL.
 * `cboId` scopes to a specific org when an admin views a client's invoice.
 */
export async function openInvoicePdf(invoiceId: string | number, cboId?: number): Promise<void> {
  const qs = cboId ? `?cbo_id=${cboId}` : '';
  const response = await fetch(`${BILLING_PREFIX}/my-billing/invoices/${invoiceId}/pdf${qs}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: 'Failed to open invoice' }));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export async function payInvoice(
  invoiceId: string,
  paymentMethodId?: string
): Promise<{ status: string; portal_url?: string; amount: number }> {
  // Unified org_invoices are paid via the hosted portal — the API returns a
  // portal_url the caller opens to complete payment.
  return fetchBilling(`/my-billing/invoices/${invoiceId}/pay`, {
    method: 'POST',
    body: JSON.stringify({ payment_method_id: paymentMethodId }),
  });
}

// --- Payment Methods ---

export interface PaymentInfo {
  card_setup_available: boolean;
  stripe_enabled: boolean;
  provisioned: boolean;
  ach: {
    available: boolean;
    bank_name?: string | null;
    account_number?: string | null;
    routing_number?: string | null;
    ach_instructions?: string | null;
  };
  check: {
    available: boolean;
    payable_to?: string | null;
    address?: string | null;
    instructions?: string | null;
  };
  payment_instructions?: string | null;
}

/** How the client can pay Trust Care: card availability + ACH/check details. */
export async function getPaymentInfo(cboId?: number): Promise<PaymentInfo> {
  return fetchBilling(`/my-billing/payment-info${cboId ? `?cbo_id=${cboId}` : ''}`);
}

export async function getPaymentMethods(): Promise<{
  payment_methods: PaymentMethod[];
  default_id?: string;
}> {
  return fetchBilling('/my-billing/payment-methods');
}

export async function createSetupIntent(): Promise<SetupIntentResponse> {
  return fetchBilling('/my-billing/payment-methods/setup', { method: 'POST' });
}

/** Start a Stripe-hosted card-setup flow; returns the URL to redirect the client to. */
export async function createCardSetupSession(cboId?: number): Promise<{ url?: string }> {
  return fetchBilling(`/my-billing/payment-methods/setup-session${cboId ? `?cbo_id=${cboId}` : ''}`, { method: 'POST' });
}

export async function removePaymentMethod(paymentMethodId: string): Promise<void> {
  await fetchBilling(`/my-billing/payment-methods/${paymentMethodId}`, {
    method: 'DELETE',
  });
}

export async function setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
  await fetchBilling(`/my-billing/payment-methods/${paymentMethodId}/default`, {
    method: 'PUT',
  });
}

// --- Usage ---

export async function getUsageSummary(period?: string): Promise<UsageSummary> {
  const qs = period ? `?period=${period}` : '';
  return fetchBilling(`/my-billing/usage${qs}`);
}

export interface PlanUsage {
  cbo_id: number;
  assigned_tier: any | null;
  counts: { seats: number; active_members: number };
  recurring: {
    billing_interval: string;
    annual: boolean;
    annual_amount: number;
    base: number;
    staff_rate: number; staff_count: number; staff_amount: number;
    member_rate: number; member_count: number; member_amount: number;
    monthly_total: number;
  } | null;
  metered_usage: { meter_code: string; meter_name: string; total_usage: number; billable_usage: number; unit_price: number; total_amount: number }[];
  metered_total: number;
  billing_provisioned: boolean;
}

/** Client-facing plan + headcount + recurring + metered usage (no provisioning needed). */
export async function getPlanUsage(cboId?: number): Promise<PlanUsage> {
  return fetchBilling(`/my-billing/plan-usage${cboId ? `?cbo_id=${cboId}` : ''}`);
}

// --- Payments ---

export async function getPaymentHistory(params?: {
  page?: number;
  page_size?: number;
}): Promise<{ payments: PaymentRecord[]; total: number; page: number; page_size: number }> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.page_size) searchParams.set('page_size', String(params.page_size));
  const qs = searchParams.toString();
  return fetchBilling(`/my-billing/payments${qs ? `?${qs}` : ''}`);
}

// --- Consumption Thresholds ---

export async function getConsumptionThresholds(): Promise<{
  thresholds: ConsumptionThreshold[];
}> {
  return fetchBilling('/my-billing/consumption/thresholds');
}

export async function updateConsumptionThresholds(data: {
  service_category: string;
  low_balance_threshold?: number;
  auto_recharge_threshold?: number;
  recharge_amount?: number;
  max_monthly_cap?: number;
  alert_enabled?: boolean;
  alert_channels?: { email: boolean; sms: boolean };
  alert_email?: string;
  alert_phone?: string;
  alert_at_percentage?: number;
  is_enabled?: boolean;
  payment_method_id?: string;
}): Promise<{ id: string; message: string }> {
  return fetchBilling('/my-billing/consumption/thresholds', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function manualRecharge(data: {
  service_category: string;
  amount: number;
  payment_method_id?: string;
}): Promise<{ payment_id: string; status: string; amount: number; service_category: string }> {
  return fetchBilling('/my-billing/consumption/recharge', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// --- Subscription ---

export async function getSubscription(): Promise<{
  subscription: BillingSubscription | null;
  items: any[];
  available_tiers: PlanTier[];
}> {
  return fetchBilling('/my-billing/subscription');
}

// --- Outcome statement (outcome-based pricing) ---

export interface OutcomeStatementRow {
  outcome_id: number;
  outcome: string;
  outcome_code: string;
  member_id: number | null;
  member_name: string;
  occurred_at: string | null;
  status: 'detected' | 'pending_verification' | 'verified' | 'billable' | 'invoiced' | 'rejected' | 'reversed';
  price: number | null;
  invoice_id: number | null;
  disputed: boolean;
  dispute_note: string | null;
  source: string;
  detail: string;
}

export interface OutcomeStatement {
  cbo_id: number;
  rows: OutcomeStatementRow[];
  summary: Record<string, { count: number; billed: number; pending: number; disputed: number }>;
}

const periodQs = (cboId?: number, periodStart?: string, periodEnd?: string) => {
  const q = new URLSearchParams();
  if (cboId) q.set('cbo_id', String(cboId));
  if (periodStart) q.set('period_start', periodStart);
  if (periodEnd) q.set('period_end', periodEnd);
  const s = q.toString();
  return s ? `?${s}` : '';
};

/** Every outcome recorded for the org in the period, with member, status and price. */
export async function getOutcomeStatement(cboId?: number, periodStart?: string, periodEnd?: string): Promise<OutcomeStatement> {
  return fetchBilling(`/my-billing/outcomes${periodQs(cboId, periodStart, periodEnd)}`);
}

/** Dispute one of the org's outcomes; it is held off invoices until resolved. */
export async function disputeOutcome(outcomeId: number, note: string, cboId?: number): Promise<{ id: number; disputed: boolean }> {
  return fetchBilling(`/my-billing/outcomes/${outcomeId}/dispute${cboId ? `?cbo_id=${cboId}` : ''}`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
}

/** Download the outcome statement as CSV. */
export async function downloadOutcomeStatement(cboId?: number, periodStart?: string, periodEnd?: string): Promise<void> {
  const res = await fetch(`${BILLING_PREFIX}/my-billing/outcomes/statement.csv${periodQs(cboId, periodStart, periodEnd)}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `outcome-statement-${periodStart || 'all'}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
